import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, CheckCircle, XCircle, Loader } from 'lucide-react';
import { authService } from '@services/authService';
import { useAuth } from '@hooks/useAuth';
import Button from '@components/common/Button';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const { loginWithToken } = useAuth();

  const [status, setStatus] = useState(token ? 'verifying' : 'pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) return;

    const verify = async () => {
      try {
        const result = await authService.verifyEmail(token);
        setStatus('success');
        // Autenticar o usuário automaticamente após verificação
        await loginWithToken(result);
      } catch (error) {
        const message = error.response?.data?.error?.message || 'Token inválido ou expirado.';
        setErrorMessage(message);
        setStatus('error');
      }
    };

    verify();
  }, [token]);

  const handleResend = async () => {
    if (!email) return;
    try {
      setResendLoading(true);
      await authService.resendVerification(decodeURIComponent(email));
      setResendSent(true);
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Erro ao reenviar e-mail.';
      setErrorMessage(message);
    } finally {
      setResendLoading(false);
    }
  };

  if (status === 'verifying') {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Loader size={48} className="text-primary-600 animate-spin" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Verificando seu e-mail...
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Aguarde enquanto confirmamos sua conta.
        </p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          E-mail verificado!
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Sua conta foi ativada com sucesso. Redirecionando...
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <XCircle size={48} className="text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Link inválido ou expirado
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {errorMessage}
        </p>
        {email ? (
          resendSent ? (
            <p className="text-green-600 dark:text-green-400 text-sm">
              Novo link enviado para <strong>{decodeURIComponent(email)}</strong>. Verifique sua caixa de entrada.
            </p>
          ) : (
            <Button onClick={handleResend} isLoading={resendLoading}>
              Reenviar e-mail de verificação
            </Button>
          )
        ) : (
          <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
            Criar nova conta
          </Link>
        )}
      </div>
    );
  }

  // status === 'pending' - usuário acabou de se registrar
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
          <Mail size={32} className="text-primary-600" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Verifique seu e-mail
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-2">
        Enviamos um link de ativação para:
      </p>
      {email && (
        <p className="font-semibold text-gray-900 dark:text-white mb-6">
          {decodeURIComponent(email)}
        </p>
      )}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Clique no link do e-mail para ativar sua conta. O link expira em 24 horas.
      </p>

      {resendSent ? (
        <p className="text-green-600 dark:text-green-400 text-sm">
          Novo link enviado! Verifique sua caixa de entrada.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nao recebeu o e-mail?
          </p>
          <Button
            variant="outline"
            onClick={handleResend}
            isLoading={resendLoading}
            disabled={!email}
          >
            Reenviar e-mail de verificacao
          </Button>
        </div>
      )}

      <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Voltar para o login
        </Link>
      </p>
    </div>
  );
};

export default VerifyEmail;
